import { useEffect, useMemo, useState } from "react";
import {
  ActionIcon, Badge, Button, Group, Modal, NavLink, NumberInput, Paper, Select,
  SimpleGrid, Stack, Text, TextInput, Title,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconPlus, IconTrash } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";

import { ApiError, api } from "../../api/client";
import type { ProductType, RecipeMoment, StockItem } from "../../api/types";
import { stockErrorMessage } from "./errors";

interface DraftLine { key: number; stock_item_id: string | null; qty: number | string; moment: RecipeMoment }

const MOMENTS: RecipeMoment[] = ["scan", "shipped", "delivered"];
let keySeq = 0;

function toDraft(pt: ProductType | undefined): DraftLine[] {
  return (pt?.lines ?? []).map((l) => ({ key: ++keySeq, stock_item_id: String(l.stock_item_id), qty: l.qty, moment: l.moment }));
}

export default function RecipesTab({ canEdit, isAdmin }: { canEdit: boolean; isAdmin: boolean }) {
  const { t } = useTranslation();
  const [types, setTypes] = useState<ProductType[]>([]);
  const [items, setItems] = useState<StockItem[]>([]);
  const [selId, setSelId] = useState<number | null>(null);
  const [lines, setLines] = useState<DraftLine[]>([]);
  const [saving, setSaving] = useState(false);
  const [newOpen, setNewOpen] = useState(false);
  const [newName, setNewName] = useState("");

  const sel = useMemo(() => types.find((x) => x.id === selId), [types, selId]);

  async function load(keepSel?: number | null) {
    try {
      const [tr, ir] = await Promise.all([api.listProductTypes(), api.listStock()]);
      setTypes(tr.types);
      setItems(ir.items);
      const id = keepSel ?? selId ?? tr.types[0]?.id ?? null;
      setSelId(id);
      setLines(toDraft(tr.types.find((x) => x.id === id)));
    } catch (e) {
      notifications.show({ color: "red", message: stockErrorMessage(e, t) });
    }
  }
  useEffect(() => { void load(); /* eslint-disable-next-line */ }, []);

  // draft-ul diferă de liniile salvate? (comparat pe conținut, nu pe chei)
  function isDirty(): boolean {
    const norm = (ls: DraftLine[]) => JSON.stringify(ls.map((l) => [l.stock_item_id, Number(l.qty), l.moment]));
    return norm(lines) !== norm(toDraft(sel));
  }

  function select(pt: ProductType) {
    if (pt.id === selId) return;
    if (canEdit && isDirty() && !window.confirm(t("stock.discardRecipeConfirm"))) return;
    setSelId(pt.id);
    setLines(toDraft(pt));
  }

  function patchLine(key: number, patch: Partial<DraftLine>) {
    setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  }

  async function save() {
    if (!sel) return;
    const out = [];
    for (const l of lines) {
      const qty = Number(l.qty);
      if (!l.stock_item_id || !(qty > 0)) {
        notifications.show({ color: "red", message: t("stock.errors.invalid_line") });
        return;
      }
      out.push({ stock_item_id: Number(l.stock_item_id), qty, moment: l.moment });
    }
    setSaving(true);
    try {
      let r;
      try {
        r = await api.saveRecipe(sel.id, out);
      } catch (e) {
        const d = e instanceof ApiError ? (e.detail as { error?: string; affected_shipments?: number; affected_units?: number } | null) : null;
        if (d?.error !== "moment_change_in_flight") throw e;
        if (!window.confirm(t("stock.momentChangeConfirm", { shipments: d.affected_shipments, units: d.affected_units }))) return;
        r = await api.saveRecipe(sel.id, out, true);
      }
      setTypes((ts) => ts.map((x) => (x.id === r.type.id ? r.type : x)));
      setLines(toDraft(r.type));
      notifications.show({ color: "green", message: t("stock.recipeSaved") });
    } catch (e) {
      notifications.show({ color: "red", message: stockErrorMessage(e, t) });
    } finally {
      setSaving(false);
    }
  }

  async function createType() {
    // tipul nou devine selectat => draft-ul curent s-ar pierde
    if (canEdit && isDirty() && !window.confirm(t("stock.discardRecipeConfirm"))) return;
    try {
      const r = await api.createProductType(newName.trim());
      setNewOpen(false);
      setNewName("");
      await load(r.type.id);
    } catch (e) {
      notifications.show({ color: "red", message: stockErrorMessage(e, t) });
    }
  }

  async function removeType() {
    if (!sel || !window.confirm(t("stock.deleteTypeConfirm", { name: sel.name }))) return;
    try {
      await api.deleteProductType(sel.id);
      notifications.show({ color: "green", message: t("stock.deleted") });
      await load(types.find((x) => x.id !== sel.id)?.id ?? null);
    } catch (e) {
      notifications.show({ color: "red", message: stockErrorMessage(e, t) });
    }
  }

  const itemOptions = items.map((i) => ({ value: String(i.id), label: `${i.name} (${i.unit})` }));
  const momentOptions = MOMENTS.map((m) => ({
    value: m,
    label: t(`stock.moment${m[0].toUpperCase()}${m.slice(1)}`),
  }));

  return (
    <SimpleGrid cols={{ base: 1, sm: 3 }}>
      <Paper withBorder p="xs">
        {types.map((pt) => (
          <NavLink
            key={pt.id}
            active={pt.id === selId}
            label={pt.name}
            opacity={pt.active ? 1 : 0.5}
            rightSection={pt.is_system ? <Badge size="xs" variant="light">{t("stock.systemType")}</Badge> : undefined}
            onClick={() => select(pt)}
          />
        ))}
        {canEdit && (
          <Button mt="xs" fullWidth variant="light" leftSection={<IconPlus size={16} />} onClick={() => setNewOpen(true)}>
            {t("stock.addProductType")}
          </Button>
        )}
      </Paper>

      <Paper withBorder p="md" style={{ gridColumn: "span 2" }}>
        {!sel ? <Text c="dimmed">{t("stock.noProductType")}</Text> : (
          <Stack>
            <Group justify="space-between">
              <Title order={4}>{t("stock.recipeFor", { name: sel.name })}</Title>
              {isAdmin && !sel.is_system && (
                <Button size="xs" variant="subtle" color="red" onClick={() => void removeType()}>{t("stock.delete")}</Button>
              )}
            </Group>
            {lines.length === 0 && <Text c="dimmed" size="sm">{t("stock.recipeEmpty")}</Text>}
            {lines.map((l) => (
              <Group key={l.key} align="flex-end" wrap="wrap" gap="xs">
                <Select
                  style={{ flex: 2 }} searchable label={t("stock.colName")} data={itemOptions}
                  value={l.stock_item_id} disabled={!canEdit}
                  onChange={(v) => patchLine(l.key, { stock_item_id: v })}
                />
                <NumberInput
                  style={{ flex: 1 }} label={t("stock.qtyPerUnit")} min={0} step={0.01} decimalScale={4}
                  value={l.qty} disabled={!canEdit}
                  onChange={(v) => patchLine(l.key, { qty: v })}
                />
                <Select
                  style={{ flex: 1 }} label={t("stock.moment")} data={momentOptions} allowDeselect={false}
                  value={l.moment} disabled={!canEdit}
                  onChange={(v) => v && patchLine(l.key, { moment: v as RecipeMoment })}
                />
                {canEdit && (
                  <ActionIcon variant="subtle" color="red" mb={4} onClick={() => setLines((ls) => ls.filter((x) => x.key !== l.key))}>
                    <IconTrash size={18} />
                  </ActionIcon>
                )}
              </Group>
            ))}
            {canEdit && (
              <Group justify="space-between">
                <Button variant="default" leftSection={<IconPlus size={16} />}
                  onClick={() => setLines((ls) => [...ls, { key: ++keySeq, stock_item_id: null, qty: 1, moment: "scan" }])}>
                  {t("stock.addLine")}
                </Button>
                <Button loading={saving} onClick={() => void save()}>{t("stock.saveRecipe")}</Button>
              </Group>
            )}
            <Text size="xs" c="dimmed">{t("stock.recipeHint")}</Text>
          </Stack>
        )}
      </Paper>

      <Modal opened={newOpen} onClose={() => setNewOpen(false)} title={t("stock.addProductType")}>
        <Stack>
          <TextInput label={t("stock.name")} value={newName} onChange={(e) => setNewName(e.currentTarget.value)} data-autofocus />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setNewOpen(false)}>{t("stock.cancel")}</Button>
            <Button disabled={!newName.trim()} onClick={() => void createType()}>{t("stock.save")}</Button>
          </Group>
        </Stack>
      </Modal>
    </SimpleGrid>
  );
}
